<template>
  <div id="scoreboard">
    <span id="player-score">Player: {{ score.player }}</span>
    <span id="cpu-score">CPU: {{ score.cpu }}</span>
  </div>
  <TheBoard
    v-if="cpu.isReady"
    v-bind:model-value="board"
    v-on:update:modelValue="updateGameState($event)"
    v-bind:player-turn="playerTurn"
    v-bind:key="board"
    ref="board"
  />
  <q-dialog v-model="isGoal" v-on:hide="resetGame">
    <q-card>
      <q-card-section class="q-pt-none">
        <div class="text-h6">You {{ hasPlayerScored ? "won" : "lost" }}!</div>
      </q-card-section>

      <q-card-actions align="center">
        <q-btn flat label="OK" color="primary" v-close-popup />
      </q-card-actions>
    </q-card>
  </q-dialog>
  <q-dialog v-model="isDraw" v-on:hide="resetGame">
    <q-card>
      <q-card-section class="q-pt-none">
        <div class="text-h6">Draw!</div>
      </q-card-section>

      <q-card-actions align="center">
        <q-btn flat label="OK" color="primary" v-close-popup />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import TheBoard from "@/components/TheBoard";
import CPU from "@/assets/scripts/cpu";
import { ConverterV1 } from "@/assets/scripts/input_converters";
import {
  MOVES,
  BALL_STATE,
  EMPTY_STATE,
  GOAL_PLACEMENT,
  EXPIRED_STATE,
  BOARD_DIMENSIONS
} from "@/constants";

export default {
  name: "Game",
  components: { TheBoard },
  data() {
    return {
      board: this.generateBoard(),
      playerTurn: true,
      score: {
        player: 0,
        cpu: 0
      },
      isGoal: false,
      hasPlayerScored: false,
      isDraw: false,
      cpu: new CPU(ConverterV1)
    };
  },
  methods: {
    generateBoard() {
      let board = Array();
      for (let column = 0; column < BOARD_DIMENSIONS.width; column++) {
        board[column] = [];
        for (let row = 0; row < BOARD_DIMENSIONS.height; row++) {
          board[column][row] = [];
          if (column === 0) {
            board[column][row][0] =
              MOVES.LEFT |
              MOVES.LEFT_TOP |
              MOVES.TOP |
              MOVES.BOTTOM |
              MOVES.LEFT_BOTTOM;
          } else if (column === 8) {
            board[column][row][0] =
              MOVES.RIGHT |
              MOVES.RIGHT_TOP |
              MOVES.TOP |
              MOVES.BOTTOM |
              MOVES.RIGHT_BOTTOM;
          } else {
            board[column][row][0] = EMPTY_STATE;
          }
          board[column][row][1] = EMPTY_STATE;
        }
      }

      board[Math.floor(BOARD_DIMENSIONS.width / 2)][
        Math.floor(BOARD_DIMENSIONS.height / 2)
      ][1] = BALL_STATE;

      const columns = [...Array(BOARD_DIMENSIONS.width - 1).keys()];
      const outlierGoalColumns = columns
        .filter(
          column => column < GOAL_PLACEMENT.begin || column > GOAL_PLACEMENT.end
        )
        .splice(1);

      outlierGoalColumns.forEach(column => {
        board[column][1][0] =
          MOVES.RIGHT |
          MOVES.RIGHT_TOP |
          MOVES.TOP |
          MOVES.LEFT_TOP |
          MOVES.LEFT;
        board[column][BOARD_DIMENSIONS.height - 2][0] =
          MOVES.RIGHT |
          MOVES.RIGHT_BOTTOM |
          MOVES.BOTTOM |
          MOVES.LEFT_BOTTOM |
          MOVES.LEFT;
      });
      board[GOAL_PLACEMENT.begin][1][0] =
        MOVES.TOP | MOVES.LEFT_TOP | MOVES.LEFT;
      board[GOAL_PLACEMENT.end][1][0] =
        MOVES.RIGHT | MOVES.RIGHT_TOP | MOVES.TOP;
      board[GOAL_PLACEMENT.begin][BOARD_DIMENSIONS.height - 2][0] =
        MOVES.BOTTOM | MOVES.LEFT_BOTTOM | MOVES.LEFT;
      board[GOAL_PLACEMENT.end][BOARD_DIMENSIONS.height - 2][0] =
        MOVES.RIGHT | MOVES.RIGHT_BOTTOM | MOVES.BOTTOM;
      board[0][1][0] = ~MOVES.RIGHT_BOTTOM & EXPIRED_STATE;
      board[BOARD_DIMENSIONS.width - 1][1][0] =
        ~MOVES.LEFT_BOTTOM & EXPIRED_STATE;
      board[0][BOARD_DIMENSIONS.height - 2][0] =
        ~MOVES.RIGHT_TOP & EXPIRED_STATE;
      board[BOARD_DIMENSIONS.width - 1][BOARD_DIMENSIONS.height - 2][0] =
        ~MOVES.LEFT_TOP & EXPIRED_STATE;

      return board;
    },
    checkPlayerChange(currentFieldValue) {
      const usedMoves = Object.values(MOVES).filter(
        moveValue => currentFieldValue & moveValue
      );
      return usedMoves.length === 1;
    },
    checkForDraw(currentFieldValue) {
      return currentFieldValue === EXPIRED_STATE;
    },
    resetGame() {
      this.board = this.generateBoard();
      this.playerTurn = true;
    },
    executeCpuMove(currentFieldValue) {
      const moveId = this.cpu.move(this.board, currentFieldValue);
      this.$refs.board.makeMove(moveId);
    },
    updateGameState(payload) {
      this.board = payload.board;
      const currentFieldValue = this.board[payload.ballPosition[0]][
        payload.ballPosition[1]
      ][0];
      if ([0, BOARD_DIMENSIONS.height - 1].includes(payload.ballPosition[1])) {
        if (payload.ballPosition[1] === 0) {
          this.score.cpu++;
          this.hasPlayerScored = false;
        } else {
          this.score.player++;
          this.hasPlayerScored = true;
        }
        this.isGoal = true;
      } else if (this.checkForDraw(currentFieldValue)) {
        this.isDraw = true;
      } else {
        if (this.checkPlayerChange(currentFieldValue)) {
          this.playerTurn = !this.playerTurn;
        }
        if (!this.playerTurn) {
          this.executeCpuMove(currentFieldValue);
        }
      }
    }
  },
  async created() {
    await this.cpu.load();
  }
};
</script>

<style scoped lang="sass">
#scoreboard
  display: flex
  flex-direction: column
  align-items: end
  font-size: 2rem
  line-height: 1.5
  padding: 0 15px
  border: 3px solid black
  position: fixed
  top: 0
  left: 0

  #player-score
    color: blue

  #cpu-score
    color: red
</style>
